import { useLayoutEffect, useMemo, useRef } from 'react';
import { BottomBar, TopBar } from '@/components/Navigation';
import Timer from '@/components/TestTimer.tsx';
import QuestionSection from '@/pages/TestPage/QuestionSection.tsx';
import AnswersSection from '@/pages/TestPage/AnswersSection.tsx';
import AnswersReviewSection from '@/pages/TestPage/AnswersReviewSection.tsx';
import MathReference from '@/components/MathReference.tsx';
import Calculator from '@/components/Calculator.tsx';
import { ComponentProps } from '@/types/component.types.ts';
import { DataProp } from 'editorjs-blocks-react-renderer';

interface TestScreenProps extends ComponentProps<DataProp> {
  question?: number;
  isPreview?: boolean;
  onQuestionChange: (q: number) => void;
  onCompleteExam: (breakAfter?: number) => void;
}

const TestScreen = ({
  question = 1,
  isPreview = false,
  onQuestionChange,
  onCompleteExam,
  duration,
  durationElapsed,
  name,
  questions,
  directions,
  breakAfter,
  componentType,
}: TestScreenProps) => {
  const currentQuestion = useMemo(() =>
    questions[question - 1]
  , [questions, question]);

  const scrollRootRef = useRef<HTMLDivElement>(null);

  // Moving between questions is a route-param change (`navigate()`), not a remount, so
  // every scroll container keeps the offset the previous question left behind and the
  // new question renders mid-page. Reset the offsets instead of remounting with
  // `key={question}`: a remount would also drop the Tabs index, the strike-through
  // toggle and the annotations toggle. `useLayoutEffect` (not `useEffect`) so the reset
  // lands before paint -- the new question is never shown scrolled.
  useLayoutEffect(() => {
    const root = scrollRootRef.current;
    if (!root) return;

    // `root` itself is the single mobile scroll container; the `[data-quiz-scroll]`
    // nodes are the landscape-phone wrappers and the desktop panes inside it.
    const containers = [root, ...root.querySelectorAll<HTMLElement>('[data-quiz-scroll]')];
    containers.forEach((el) => { el.scrollTop = 0; });
  }, [question]);

  return (
    <>
      <TopBar
        isPreview={isPreview}
        title={name}
        rightSlot={
          <>
            <MathReference />
            {/*`key` is used to reset the state of the calculator when question changes*/}
            <Calculator key={question} />
          </>
        }
      >
        {
          Boolean(duration) &&
          <Timer duration={duration} elapsed={durationElapsed} onComplete={onCompleteExam} readOnly={isPreview} />
        }
      </TopBar>
      <div
        ref={scrollRootRef}
        data-quiz-scroll-root
        className="flex flex-1 min-h-0 m:flex-col m:overflow-y-auto
                    ml:flex-row ml:overflow-hidden"
      >
        {
          currentQuestion?.question ? (
            <>
              <div
                data-quiz-scroll
                className="contents ml:flex ml:flex-1 ml:min-w-0 ml:overflow-y-auto"
              >
                <QuestionSection
                  categories={currentQuestion.categories}
                  question={currentQuestion.question}
                  examQuestionId={currentQuestion.examQuestionId}
                  clientExamQuestionId={currentQuestion.clientExamQuestionId}
                  directions={directions}
                  explanation={currentQuestion.explanation}
                  readOnly={isPreview}
                  componentType={componentType}
                  annotations={currentQuestion.annotations}
                />
              </div>

              {/* `m:w-full` + `m:mx-4` is 100% of the root *plus* 32px of margin, so the
                  margin box is 32px wider than the mobile scroll root and the whole
                  quiz can be panned sideways. `m:w-auto` + stretch keeps the 16px
                  gutters without the overflow. */}
              <div className="bg-[#44413C] w-1 my-10 rounded-md m:h-1 m:w-auto m:my-0 m:mx-4
                              ml:h-full ml:w-px ml:my-0 ml:mx-0" />

              <div
                data-quiz-scroll
                className="contents ml:flex ml:flex-1 ml:min-w-0 ml:overflow-y-auto"
              >
                {
                  isPreview
                    ? <AnswersReviewSection currentQuestion={currentQuestion} />
                    : <AnswersSection currentQuestion={currentQuestion} />
                }
              </div>
            </>
          ) : (
            <strong className="self-center flex-1 text-center">
              Something is wrong with the question!
            </strong>
          )
        }
      </div>
      <BottomBar
        title={`${name} Questions`}
        isPreview={isPreview}
        questions={questions}
        currentQuestion={question}
        onPrevQuestion={() => onQuestionChange(question - 1)}
        onNextQuestion={() => onQuestionChange(question + 1)}
        onQuestionSelect={onQuestionChange}
        onCompleteExam={() => onCompleteExam(breakAfter)}
      />
    </>
  );
};

export default TestScreen;
